import { Network } from "@aptos-labs/ts-sdk";
import { DecibelReadDex, NAMED_CONFIGS } from "@decibeltrade/sdk";
import { isServer } from "@tanstack/react-query";

import { capitalize } from "@/utils/format";
import { Logger } from "@/utils/logger";

import { env } from "../env";

export function getSdkConfig() {
  const configName = env.NEXT_PUBLIC_SDK_CONFIG_NAME;
  if (!NAMED_CONFIGS[configName]) {
    throw new Error(`Invalid config: ${configName}`);
  }
  let baseConfig = NAMED_CONFIGS[configName];

  // Throwaway FFT campaign deploys point the campaign surface (FFT + rewards
  // claims) at a different package + campaign object than the network's
  // shared deployment. Dev/test only.
  if (env.NEXT_PUBLIC_FFT_CAMPAIGN_PACKAGE || env.NEXT_PUBLIC_FFT_CAMPAIGN_ADDR) {
    baseConfig = {
      ...baseConfig,
      deployment: {
        ...baseConfig.deployment,
        campaignPackage:
          env.NEXT_PUBLIC_FFT_CAMPAIGN_PACKAGE ?? baseConfig.deployment.campaignPackage,
        fftCampaignAddr: env.NEXT_PUBLIC_FFT_CAMPAIGN_ADDR,
      },
    };
  }

  // Point the SDK at a locally-run trading-api (FFT smoke rig). Dev/test only.
  if (env.NEXT_PUBLIC_TRADING_HTTP_URL || env.NEXT_PUBLIC_TRADING_WS_URL) {
    baseConfig = {
      ...baseConfig,
      tradingHttpUrl: env.NEXT_PUBLIC_TRADING_HTTP_URL ?? baseConfig.tradingHttpUrl,
      tradingWsUrl: env.NEXT_PUBLIC_TRADING_WS_URL ?? baseConfig.tradingWsUrl,
    };
  }

  return {
    ...baseConfig,
    gasStationApiKey: env.NEXT_PUBLIC_GAS_STATION_API_KEY,
    // Fee-payer address for encrypted (sponsored) transactions. The SDK no
    // longer ships built-in addresses; the frontend supplies its own.
    gasStationAddress: env.NEXT_PUBLIC_GAS_STATION_ADDRESS,
  };
}

// A gas station without a fee-payer address silently disables encrypted
// submission (the address is baked into the encrypted payload's AEAD associated
// data at build time — see `configSupportsEncryptedSubmission` in the SDK). The
// UI degrades honestly to "Encryption Paused", but the misconfiguration itself
// is invisible otherwise, so surface it once per boot.
if (env.NEXT_PUBLIC_GAS_STATION_API_KEY && !env.NEXT_PUBLIC_GAS_STATION_ADDRESS) {
  Logger.error(
    new Error(
      "Gas station configured without NEXT_PUBLIC_GAS_STATION_ADDRESS — transactions will fall back to plaintext",
    ),
    { feature: "encryption" },
  );
}

export function getSdkNetworkName(config: { network: Network }) {
  // Localnet and docker both run as CUSTOM; the wallet has no matching network
  // name for either, so label them uniformly.
  return config.network === Network.CUSTOM ? "Local" : capitalize(config.network);
}

// Header names must match Rust constants in rust/*/src/internal_headers.rs
// (HEADER_ORG_ID, HEADER_PROJECT_ID, HEADER_APP_ID)
function getInternalHeaders(): Record<string, string> | undefined {
  if (!isServer) return undefined;
  const orgId = env.SERVER_INTERNAL_ORG_ID;
  const projectId = env.SERVER_INTERNAL_PROJECT_ID;
  const appId = env.SERVER_INTERNAL_APP_ID;
  if (!orgId || !projectId || !appId) return undefined;
  return {
    "x-internal-organization-id": orgId,
    "x-internal-project-id": projectId,
    "x-internal-application-id": appId,
  };
}

function makeReadDexSdk() {
  const config = getSdkConfig();
  const additionalHeaders = getInternalHeaders();

  return new DecibelReadDex(additionalHeaders ? { ...config, additionalHeaders } : config, {
    nodeApiKey: additionalHeaders
      ? undefined
      : isServer
        ? env.SERVER_API_KEY
        : env.NEXT_PUBLIC_API_KEY,
    onChainFallback: ({ method, error }) =>
      Logger.warn(`SDK ${method} fell back to chain views`, { feature: "fft", error }),
  });
}

export const readDexSdk = makeReadDexSdk();
export const sdkConfig = readDexSdk.config;
export const network = sdkConfig.network;
export const predepositPackage = sdkConfig.deployment.predepositPackage;

export function getSolanaConfig() {
  const isMainnet = network === Network.MAINNET;
  return {
    rpc:
      env.NEXT_PUBLIC_SOLANA_RPC_URL ||
      (isMainnet
        ? "https://loni-kx8vfe-fast-mainnet.helius-rpc.com"
        : "https://api.devnet.solana.com"),
    serverClaimUrl: env.NEXT_PUBLIC_SOLANA_CLAIM_SERVER_URL,
  };
}
